import { Student } from './../models/student';
import { Serie } from '../models/serie';
import { Session } from '../models/session';

import { Injectable } from '@angular/core';

import { Initializable } from './app-init.service';
import { SeriesService } from './series.service';
import { QuestionsService } from './questions.service';
import { SessionsService } from './sessions.service';
import { StudentService } from './student.service';
import { SentryService } from './sentry.service';
import { UserErrorHandlerService } from './user-error-handler.service';
import { CdrVersionService } from './cdr-version.service';

import { switchMap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { Platform } from '@ionic/angular/standalone';
import { StorageService } from './storage.service';

import { EasyDebugDecorator } from '../../app/decorators/easy-debug.decorator';

import moment from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
@EasyDebugDecorator
export class SyncCDRService implements Initializable {
  syncSeriesToSync = 0;
  syncSeriesSynced = 0;
  syncStartTimer = 0;
  syncEndTimer = 0;
  syncTimeSpent = 0;

  constructor(
    private seriesService: SeriesService,
    private questionsService: QuestionsService,
    private sessionsService: SessionsService,
    private studentService: StudentService,
    private platform: Platform,
    private storageService: StorageService,
    private sentryService: SentryService,
    private userErrorHandlerService: UserErrorHandlerService,
    private cdrVersionService: CdrVersionService
  ) {}

  async init() {
    const student = this.studentService.student;
    const checkCdrVersion = await this.cdrVersionService.isMajRequired();
    if (checkCdrVersion) {
      return null;
    }
    try {
      const series = await this.sync(student);
      if (!series) {
        return null;
      }
      if (!!series.errorCode && series.errorCode === 'E301') {
        this.userErrorHandlerService.addError({
          criticity: 20,
          service: 'sync-cdr',
          platform: 'device',
          data: series.errorOriginal,
          errorCode: 'scss',
        });
      }
      await this.reconcileApisData();
      return series;
    } catch (err) {
      this.userErrorHandlerService.addError({
        criticity: 20,
        service: 'sync-cdr',
        platform: 'device',
        data: err,
        errorCode: 'scss',
      });
      this.sentryService.sendToSentry('Sync CDR: Failed: ' + JSON.stringify(err), this.studentService.student);
    }
  }

  dateToFormatFr(input?: any) {
    // console.log('-------------------');
    // console.log('dateToFormatFr input', input, typeof input);
    let output = this.dateToMoment(); // today by default
    if (!!input) {
      output = this.dateToMoment(input);
      if (typeof input === 'string') {
        output = this.dateToMoment(input);
        // if (!input.includes('+')) {
        //   console.log('input string', input);
        // }
        if (input.includes('/')) {
          console.error('input string', input);
        }
        // console.log('output', output);
        // console.log('-------------------');
      } else if (typeof input === 'number') {
        // timestamp
        output = this.dateToMoment(input);
        // console.log('input timestamp', input);
        // console.log('output', output);
        // console.log('-------------------');
      } else if (typeof input === 'object') {
        if (!moment.isMoment(input) && !!input.year && !!input.month && !!input.day) {
          // custom object
          const year = input.year;
          const month = Number(input.month) < 10 ? '0' + Number(input.month) : Number(input.month);
          const day = input.day < 10 ? '0' + input.day : input.day;
          const str = year + '-' + month + '-' + day + ' 00:00:00';
          output = this.dateToMoment(str);
          // console.log('input custom object', input, str);
          // console.log('output', output);
          // console.log('-------------------');
        } else {
          // date object
          output = this.dateToMoment(input);
          // console.log('input date', input, input.isValid());
          // console.log('output', output);
        }
      }
    }
    if (!output.isValid()) {
      console.log('-------------------');
      console.error('dateToFormatFr input', input, typeof input);
      console.error('dateToFormatFr output', output, output.toString(), output.isValid());
      console.error('-------------------');
    }
    // console.log('dateToFormatFr output', output, output.toString(), output.isValid());
    // console.log('-------------------');
    return output;
  }

  dateToMoment(input?: any) {
    const zone = 'Europe/Paris';
    moment.tz.setDefault(zone);
    moment.locale('fr');
    if (!!input) {
      return moment(input).tz(zone);
    }
    return moment().tz(zone);
  }

  async reconcileApisData() {
    const student = this.studentService.student;
    let series = [];
    let sessions = [];
    const resSeriesList = await this.seriesService.getSeriesList(student);
    if (!!resSeriesList && !!resSeriesList.errorCode && resSeriesList.errorCode === 'E301') {
      series = [];
    } else {
      series = resSeriesList;
    }
    const resgetSessions = await this.sessionsService.getSessions(student);
    if (!!resgetSessions && !!resgetSessions.errorCode && resgetSessions.errorCode === 'E301') {
      sessions = [];
    } else {
      sessions = resgetSessions;
    }
    // order sessions by date desc
    sessions.sort((a, b) => {
      // DATE TO CHANGE
      return +new Date(b.lastAnsweredAt) - +new Date(a.lastAnsweredAt);
    });
    if (!!sessions && sessions.length > 0) {
      await this.updateSeriesLastSessions(student, series, sessions);
    }
  }

  async updateSeriesLastSessions(student: any, series: any, sessions: any) {
    const updatedSeries: Serie[] = [];
    if (!!series && series.length > 0) {
      for (const serie of series) {
        const updatedSerie: Serie = new Serie(serie);
        const _lastTrainingSessions: any[] = JSON.parse(JSON.stringify(sessions));
        const _lastExamSessions: any[] = JSON.parse(JSON.stringify(sessions));
        const lastTrainingSessions = _lastTrainingSessions.filter(
          (elt) => elt._serieId === serie.id && elt._mode === 'Cdr::TrainingSession' && elt._answersCount > 0
        );
        const lastExamSessions = _lastExamSessions.filter(
          (elt) => elt._serieId === serie.id && elt._mode === 'Cdr::ExamSession' && elt._answersCount > 0
        );
        const lastTrainingSession =
          !!lastTrainingSessions && lastTrainingSessions.length > 0 ? lastTrainingSessions[0]._id : null;
        const lastExamSession = !!lastExamSessions && lastExamSessions.length > 0 ? lastExamSessions[0]._id : null;
        updatedSerie.lastSessions = [lastTrainingSession, lastExamSession];
        updatedSeries.push(updatedSerie);
      }
      await this.seriesService.setSeriesList(updatedSeries, student);
    }
  }

  async updateSerieData(student: any, serie: any, sessions: any) {
    if (!!serie && !!student) {
      const updatedSerie: Serie = new Serie(serie);
      if (!sessions) {
        const resgetSessions = await this.sessionsService.getSessions(student);
        if (!!resgetSessions && !!resgetSessions.errorCode && resgetSessions.errorCode === 'E301') {
          sessions = [];
        } else {
          sessions = resgetSessions;
        }
      }
      const _lastTrainingSessions: any[] = JSON.parse(JSON.stringify(sessions));
      const _lastExamSessions: any[] = JSON.parse(JSON.stringify(sessions));
      const lastTrainingSessions = _lastTrainingSessions.filter(
        (elt) => elt._serieId === serie.id && elt._mode === 'Cdr::TrainingSession' && elt._answersCount > 0
      );
      const lastExamSessions = _lastExamSessions.filter(
        (elt) => elt._serieId === serie.id && elt._mode === 'Cdr::ExamSession' && elt._answersCount > 0
      );
      const lastTrainingSession =
        !!lastTrainingSessions && lastTrainingSessions.length > 0 ? lastTrainingSessions[0]._id : null;
      const lastExamSession = !!lastExamSessions && lastExamSessions.length > 0 ? lastExamSessions[0]._id : null;
      updatedSerie.lastSessions = [lastTrainingSession, lastExamSession];
      return await this.seriesService.setSerie(updatedSerie, student);
    }
    return serie;
  }

  formatDebug(data: any) {
    let str = '';
    if (!!data) {
      if (Array.isArray(data)) {
        data.forEach(function (entry: object) {
          str = str !== '' ? str + '\n ######### \n' : str;
          if (typeof entry === 'object' && !Array.isArray(entry) && entry !== null) {
            for (const [key, value] of Object.entries(entry)) {
              if (!!key && !!value) {
                if ((key === '_answers' || key === 'answers') && Array.isArray(value)) {
                  str += `${key}: ${value.length} - `;
                } else {
                  str += `${key}: ${JSON.stringify(value)} - `;
                }
              }
            }
          }
        });
      } else {
        if (typeof data === 'object' && !Array.isArray(data) && data !== null) {
          for (const [key, value] of Object.entries(data)) {
            if (!!key && !!value) {
              if ((key === '_answers' || key === 'answers') && Array.isArray(value)) {
                str += `${key}: ${value.length} - `;
              } else {
                str += `${key}: ${JSON.stringify(value)} - `;
              }
            }
          }
        } else {
          str += JSON.stringify(data) + ' - ';
        }
      }
    }
    return str;
  }

  async sync(student: Student): Promise<any> {
    const locked = !(await this.storageService.checkEnougthSpace());
    const checkCdrVersion = await this.cdrVersionService.isMajRequired();
    if (!this.platform.is('cordova') || locked || !checkCdrVersion) {
      return null;
    }
    return from(of(null))
      .pipe(
        switchMap(async () => {
          this.syncStartTimer = Date.now();
          let lastUpdate = null;
          const sessions = await this.sessionsService.getSessions(student, true);
          if (!!sessions && sessions.length > 0) {
            const sessionsSorted = sessions.sort((eltf, elts) => {
              // DATE TO CHANGE
              return +new Date(elts.lastAnsweredAt) - +new Date(eltf.lastAnsweredAt);
            });
            lastUpdate = sessionsSorted.shift().lastAnsweredAt;
          }
          return this.syncSessionsPromise(lastUpdate, student);
        }),
        switchMap(async () => {
          let lastUpdate = null;
          // console.log('sync-cdr sync-series');
          const series = await this.seriesService.getSeries(student, true);

          // console.log('series => ', series);
          if (!!series && series.length > 0) {
            const seriesSorted = series.sort((eltf, elts) => {
              // DATE TO CHANGE
              return +new Date(elts.updatedAt) - +new Date(eltf.updatedAt);
            });
            // console.log('series sorted => ', JSON.parse(JSON.stringify(seriesSorted)));
            lastUpdate = seriesSorted.shift().updatedAt;
            // console.log('lastupdatedat => ', lastUpdate);
          }
          return this.syncSeriesPromise(lastUpdate, student);
        }),
        switchMap(async (series) => {
          // console.log('sync-cdr sync series', JSON.parse(JSON.stringify(series)));
          // en fait le get va ensuite faire un set => moche !!
          await this.sessionsService.getSessionsType(student);
          return series;
        }),
        switchMap(async (series) => {
          await this.updateLastSessions(student);
          this.syncEndTimer = Date.now();
          this.syncTimeSpent = (this.syncEndTimer - this.syncStartTimer) / 1000;
          return series;
        }),
        switchMap(async (series) => {
          let lastUpdatedQuestion = null;
          const questions = await this.questionsService.getQuestions(student, true);
          // console.log('local questions => ', questions);
          if (!!questions && questions.length > 0) {
            const questionsSorted = questions.sort((eltf, elts) => {
              // DATE TO CHANGE
              return +new Date(elts.updatedAt) - +new Date(eltf.updatedAt);
            });
            lastUpdatedQuestion = questionsSorted.shift().updatedAt;
            // console.log('last update question => ', lastUpdatedQuestion);
            if (!!lastUpdatedQuestion) {
              await this.questionsService.syncQuestions(student, lastUpdatedQuestion, questions);
            }
          } else {
            // donc en gros si pas de questions en local tu récup tout et tu save dfans le storage ?
            // meme en guest !!!
            await this.questionsService.getQuestions(student);
          }
          return series;
        }),
        switchMap(async (series) => {
          if (!!student.status && student.status !== 'guest') {
            await this.seriesService.syncThemes(student);
          }
          return series;
        })
      )
      .toPromise();
  }

  resetSyncCdrStats() {
    this.syncSeriesToSync = 0;
    this.syncSeriesSynced = 0;
    this.syncStartTimer = 0;
    this.syncEndTimer = 0;
    this.syncTimeSpent = 0;
  }

  syncSessionsPromise(updatedAt: string, student: Student): Promise<Session[]> {
    if (!!student && !!updatedAt) {
      return this.sessionsService.syncSessions(updatedAt, student).toPromise();
    }
    return new Promise((resolve) => resolve([]));
  }

  syncSeriesPromise(updatedAt: string, student: Student): Promise<Serie[]> {
    if (!!student && !!updatedAt) {
      return this.seriesService.syncSeries(updatedAt, student).toPromise();
    }
    return new Promise((resolve) => resolve([]));
  }

  async updateLastSessions(student: Student) {
    if (!!student) {
      const lSessions = await this.sessionsService.getSessions(student, true);
      // console.log('sync-cdr updateLastSessions lSessions', JSON.parse(JSON.stringify(lSessions)));
      const lSeriesList = await this.seriesService.getSeriesList(student, true);
      // console.log('sync-cdr updateLastSessions lSeriesList', JSON.parse(JSON.stringify(lSeriesList)));
      if (!!lSessions && lSessions.length > 0) {
        for (const lSession of lSessions) {
          const index = !!lSession.mode && lSession.mode === 'Cdr::ExamSession' ? 1 : 0;
          if (!!lSeriesList && !!lSession.serieId && !!lSession.id && !!lSession.lastAnsweredAt) {
            const serieFound = lSeriesList.find((elt) => elt.id === lSession.serieId);
            // console.log('sync-cdr updateLastSessions serieFound', JSON.parse(JSON.stringify(serieFound)));
            if (!!serieFound && !!serieFound.id) {
              const serie = await this.seriesService.getSerie(serieFound.id, student, true, true);
              if (!!serie && !!serie.lastSessions) {
                let localSerieFound = null;
                if (serie.lastSessions[index] === null) {
                  serie.lastSessions[index] = lSession.id;
                  localSerieFound = lSeriesList.find((elt) => elt.id === lSession.serieId);
                  if (!!localSerieFound && !!localSerieFound.lastSessions[index]) {
                    localSerieFound.lastSessions[index] = lSession.id;
                    const seriesListIndex = lSeriesList.findIndex((elt) => elt.id === localSerieFound.id);
                    if (seriesListIndex !== 1) {
                      lSeriesList[seriesListIndex] = localSerieFound;
                    }
                  }
                } else {
                  const session = lSessions.find((elt) => elt.id === serie.lastSessions[index]);
                  // DATE TO CHANGE
                  if (
                    !!session &&
                    !!session.lastAnsweredAt &&
                    this.dateToFormatFr(lSession.lastAnsweredAt).valueOf() >
                      this.dateToFormatFr(session.lastAnsweredAt).valueOf()
                  ) {
                    serie.lastSessions[index] = lSession.id;
                    localSerieFound = lSeriesList.find((elt) => elt.id === lSession.serieId);
                    if (!!localSerieFound && !!localSerieFound.lastSessions[index]) {
                      localSerieFound.lastSessions[index] = lSession.id;
                      const seriesListIndex = lSeriesList.findIndex((elt) => elt.id === localSerieFound.id);
                      if (seriesListIndex !== -1) {
                        lSeriesList[seriesListIndex] = localSerieFound;
                      }
                    }
                  }
                }
                await this.seriesService.setSerie(serie, student);
              }
            }
          }
        }
      }
      if (!!lSeriesList && lSeriesList.length > 0) {
        // console.log('sync-cdr updateLastSessions lSeriesList', JSON.parse(JSON.stringify(lSeriesList)));
        return await this.seriesService.setSeriesList(lSeriesList, student);
      }
      return [];
    }
    this.sentryService.sendToSentry('SyncCDR updateLastSessions: student null', this.studentService.student);
    return [];
  }
}
